import apiClient from '../client';

export interface PatientFormData {
  email: string;
  first_name: string;
  last_name: string;
  address: string;
  phone: string;
  dob: string;
  gender: string;
  occupation: string;
  referred_by: string;
  language: string;
  communication_preference: string;
  conditions?: string;
  medical_info?: {
    medicare: number;
    phone: string;
    cur_medications: string;
    medical_history: string;
    prev_treatments: string;
    emergency_contact: string;
    patient_relation: string;
    patient_profile: string;
    emergency_phone: string;
    insurance_provider: string;
    insurance_number: number;
    insurance_group: string;
    document_loc: string;
    allergies: string;
  };
}

// Interfaces based on the API documentation
export interface TreatmentPlan {
  id: number;
  name: string;
  status: 'ACTIVE' | 'COMPLETED' | 'PAUSED' | 'FLAGGED';
}

export interface Patient {
  id?: number;
  first_name: string;
  last_name: string;
  address: string;
  dob: string | null;
  email: string;
  phone: string;
  occupation: string;
  referred_by: string;
  language: string;
  clinic: number | null;
  user: number;
  medical_info: {
    id: number;
    medicare: number;
    emergency_phone: string;
    cur_medications: string;
    medical_history: string;
    prev_treatments: string;
    emergency_contact: string;
    patient_relation: string;
    insurance_provider: string;
    insurance_number: number;
    insurance_group: string;
    document_loc: string;
    allergies: string;
    notes?: string | null;
    conditions?: string | null;
  };
  treatment_plans: TreatmentPlan[];
}

export interface MedicalInfoUpdate {
  medical_info: {
    notes?: string;
    conditions?: string;
  };
}

export interface PatientListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Patient[];
  message?: string;
}

export interface PatientAnalytics {
  patient_data: {
    patient_name: string;
    treatment_plans: {
      id: number;
      name: string;
      description: string;
      completion: string;
    }[];
    patient_status: string;
    last_activity_date: string;
  }[];
  analytics: {
    current_month: string;
    previous_month: string;
    active_patients: number;
    current_month_patients_on_treatment: number;
    prev_month_patients_on_treatment: number;
    patient_increase: number;
    patient_increase_percentage: number;
    current_adherence_rate: number;
    prev_adherence_rate: number;
    adherence_increase: number;
  };
}
export interface PatientAnalyticsResponse {
  message?: string;
  data: PatientAnalytics;
}
export const PatientApi = {
  /**
   * Get all patients
   * @returns List of patients
   */
  getAll: async (): Promise<PatientListResponse> => {
    const response = await apiClient.get('/patients/');
    return response.data;
  },

  /**
   * Create a new patient
   * @param patient Patient data to create
   * @returns Created patient data
   */
  createPatient: async (patient: PatientFormData): Promise<PatientListResponse> => {
    const response = await apiClient.post('/patients/', patient);
    return response.data;
  },

  /**
   * Get a single patient by ID
   * @param patientId ID of the patient to retrieve
   * @returns Patient data
   */
  getById: async (patientId: number | string): Promise<PatientListResponse> => {
    const response = await apiClient.get(`/patients/${patientId}/`);
    return response.data;
  },

  /**
   * Update a patient
   * @param patientId ID of the patient to update
   * @param patientData Partial patient data to update
   * @returns Updated patient data
   */
  updatePatient: async (
    patientId: number | string,
    patientData: Partial<Patient>
  ): Promise<PatientListResponse> => {
    const response = await apiClient.patch(`/patients/${patientId}/`, patientData);
    return response.data;
  },

  /**
   * Update patient medical information
   * @param patientId ID of the patient to update
   * @param medicalInfo Medical information to update
   * @returns Updated patient data
   */
  updateMedicalInfo: async (
    patientId: number | string,
    medicalInfo: MedicalInfoUpdate
  ): Promise<PatientListResponse> => {
    const response = await apiClient.patch(`/patients/${patientId}/`, medicalInfo);
    return response.data;
  },

  /**
   * Delete a patient
   * @param patientId ID of the patient to delete
   * @returns Deleted patient data with success message
   */
  deletePatient: async (patientId: number | string): Promise<PatientListResponse> => {
    const response = await apiClient.delete(`/patients/${patientId}/`);
    return response.data;
  },

  /**
   * Get patient analytics
   * @returns Patient analytics data
   */
  getAnalytics: async (): Promise<PatientAnalyticsResponse> => {
    const response = await apiClient.get('/patients/analytics/');
    return response.data;
  },
};
